import { useIntl } from "react-intl";
import { throwForValidationResult } from "@/lib/graphql-error";
import { useCompanyContext } from "@/pages/company/use-company-context";
import { actingAsDeclaration } from "@/lib/share-item";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateSharedItemCommentDocument,
  DeleteSharedItemCommentDocument,
  SharedItemCommentsDocument,
  UpdateSharedItemCommentDocument,
  type SharedItemCommentsQuery,
} from "@/graphql/generated/operations";

export type SharedItemComment = NonNullable<
  NonNullable<SharedItemCommentsQuery["sharedItemComments"]>["nodes"]
>[number];

export interface UseSharedItemCommentsResult {
  comments: readonly SharedItemComment[];
  totalCount: number;
  loading: boolean;
  error: Error | null;
  busy: boolean;
  add: (content: string, companyId?: string | null) => Promise<void>;
  edit: (commentId: string, content: string) => Promise<void>;
  remove: (commentId: string) => Promise<void>;
  refetch: () => Promise<unknown>;
}

export function useSharedItemComments(itemId: string): UseSharedItemCommentsResult {
  const intl = useIntl();
  const query = useQuery(SharedItemCommentsDocument, {
    // Newest first, capped at 50 (no "load more" UI yet). Cast around
    // codegen's nominal SortOrder split.
    variables: {
      input: {
        first: 50,
        where: { sharedItemId: { eq: itemId } },
        order: { postedAt: "DESC" },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any,
    skip: !itemId,
  });

  const [createMut, createState] = useMutation(CreateSharedItemCommentDocument);
  const [updateMut, updateState] = useMutation(UpdateSharedItemCommentDocument);
  const [deleteMut, deleteState] = useMutation(DeleteSharedItemCommentDocument);
  // THE DECLARATION CONCEPT v23 ASKS FOR (the company account model, Kevin
  // 2026-09-11, extended 2026-09-12). The declared acting context and the ownership of the comment
  // must MATCH: a COMPANY comment carries the company it is filed under — absent or different is
  // GENESIS001259 — and a PERSONAL comment carries nothing at all, since a declaration over one's own
  // words is GENESIS001260.
  //
  // READ HERE RATHER THAN PASSED IN, so no caller can forget it, and READ RESOLVED: `useCompanyContext`
  // answers only for a membership row genesis returned with `role = admin`. It authorizes nothing —
  // the governing company is the one stored on the row and the `company_membership` check is
  // unchanged; this only has to agree with it (genesis#300).
  const { active: actingAsCompany } = useCompanyContext();

  const commentFailed = intl.formatMessage({
    id: "share.comment.saveFailed",
    defaultMessage: "The comment could not be saved.",
  });

  const comments = query.data?.sharedItemComments?.nodes ?? [];
  const totalCount = query.data?.sharedItemComments?.totalCount ?? comments.length;

  // Declare the resolved mode independently of the requested content ownership.
  const declaration = actingAsDeclaration(actingAsCompany?.id ?? null);

  async function add(content: string, companyId?: string | null) {
    const trimmed = content.trim();
    if (!trimmed) return;
    // genesis liest nur data.sharedItem (Relation-Input); das Schwesterfeld
    // sharedItemId existiert zwar im Schema, fuehrt serverseitig aber zu einer NPE.
    const created = await createMut({
      variables: {
        input: {
          data: {
            sharedItem: { id: itemId },
            content: trimmed,
            ...(companyId ? { company: { id: companyId } } : {}),
          },
          ...declaration,
        },
      },
      // KEEP THE PARENT COUNTER HONEST — the mirror of `remove` below, which has done this all along
      // while this path did not. `query.refetch()` reloads the THREAD; it never refetches the
      // SharedItem that carries `comments`, so without this the detail header and the list cards keep
      // showing the old number until something else happens to reload the item.
      update(cache, { data }) {
        // ONLY on the success member. Both union members resolve, so a refused create reaches here
        // too, and it must not bump a counter for a comment that was never written.
        if (data?.createSharedItemComment?.__typename !== "CreateSharedItemCommentResult") return;
        cache.modify({
          id: cache.identify({ __typename: "SharedItem", id: itemId }),
          fields: {
            // NULL STAYS NULL. genesis answers `comments` as null for a reader outside the nine-field
            // browse whitelist, and spark renders that as 0 — turning it into 1 here would invent a
            // count the server deliberately withheld, and only for the person who just posted.
            comments: (prev: number | null) => (typeof prev === "number" ? prev + 1 : prev),
          },
        });
      },
    });
    // Both union members are selected, so a rejection resolves. Without this the comment simply never
    // appeared and the user was told nothing.
    const out = created.data?.createSharedItemComment;
    if (out?.__typename !== "CreateSharedItemCommentResult") {
      throwForValidationResult(out, commentFailed);
    }
    await query.refetch();
  }

  async function edit(commentId: string, content: string) {
    const trimmed = content.trim();
    if (!trimmed) return;
    const updated = await updateMut({
      variables: {
        id: commentId,
        input: { data: { content: trimmed }, ...declaration },
      },
    });
    const out = updated.data?.updateSharedItemComment;
    if (out?.__typename !== "UpdateSharedItemCommentResult") {
      throwForValidationResult(out, commentFailed);
    }
    await query.refetch();
  }

  async function remove(commentId: string) {
    await deleteMut({
      variables: { input: { id: commentId, ...declaration } },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: "SharedItemComment", id: commentId }) });
        // Keep the parent SharedItem's comments counter honest so any other
        // view showing it (detail header, list cards) stays in sync without
        // needing SharedItemDetail / SharedItems to refetch.
        cache.modify({
          id: cache.identify({ __typename: "SharedItem", id: itemId }),
          fields: {
            comments: (prev: number) => Math.max(0, (prev ?? 0) - 1),
          },
        });
        cache.gc();
      },
    });
    await query.refetch();
  }

  return {
    comments,
    totalCount,
    loading: query.loading,
    // THE READ'S ERROR ONLY, exactly as `useSharedItem` reports it and for the same reason. The page
    // BRANCHES on this value and renders "Error: …" INSTEAD of the thread, so folding a refused
    // WRITE in here blew the whole discussion off the screen and left it there — nothing calls
    // `reset()`. That became unacceptable with concept v16 (the company account model), which freezes an inactive
    // company's VOICE and says in the same breath "Gesperrt ist die Firmenstimme, nicht die
    // Diskussion. Der Thread bleibt lesbar": a refused company comment must not take the thread with
    // it. A rejected write rejects the promise the caller awaited, which is where it belongs — the
    // composer names the refusal beside itself and the list keeps rendering.
    error: (query.error as Error | undefined) ?? null,
    busy: createState.loading || updateState.loading || deleteState.loading,
    add,
    edit,
    remove,
    refetch: () => query.refetch(),
  };
}
